/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Tooltip,
  Space,
  Modal,
  message,
  Input,
  Form,
  Layout,
  Row,
  Col,
  DatePicker,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  CloseOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileImageOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../../services/axiosConfig';

const { Search } = Input;
const { Title, Text } = Typography;
const { Header, Content } = Layout;

const EmailReportForm = ({ record, onSend, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSend({
        emails: values.emails.split(',').map((email) => email.trim()),
        subject: values.subject,
        message: values.message,
      });
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        subject: `Heat Profile Report - ${record?.reportNumber || 'N/A'}`,
        message: 'Please find attached the heat profile report for review and approval.',
      }}
    >
      <Form.Item
        label="Recipient Emails (comma separated)"
        name="emails"
        rules={[
          { required: true, message: 'Please enter at least one email address' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const emails = value.split(',').map((email) => email.trim());
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              const invalidEmails = emails.filter((email) => !emailRegex.test(email));
              return invalidEmails.length > 0
                ? Promise.reject(new Error(`Invalid email(s): ${invalidEmails.join(', ')}`))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input.TextArea
          placeholder="engineer@pipeline-technique.com, manager@pipeline-technique.com"
          rows={2}
        />
      </Form.Item>

      <Form.Item
        label="Subject"
        name="subject"
        rules={[{ required: true, message: 'Please enter email subject' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Message"
        name="message"
        rules={[{ required: true, message: 'Please enter email message' }]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Send Email
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

const ProjectHeatProfileList = (props) => {
  const { selectedProject, selectedSOW, onEdit, onCreateNew, setIsHeatProfileModalVisible } = props;

  // Internal back handler: closes the list modal if setVisible is provided
  const onBack = () => {
    setIsHeatProfileModalVisible(false);
  };

  // State variables
  const [projectHeatProfiles, setProjectHeatProfiles] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: '',
    material: '',
    trialDate: '',
    createdDate: '',
  });

  // Modal states
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  // Fetch project heat profiles
  useEffect(() => {
    if (selectedProject?.id) {
      const fetchProjectHeatProfiles = async () => {
        setIsLoadingData(true);
        try {
          console.log('Fetching project heat profiles for project:', selectedProject.id);
          const response = await axiosConfig.get('/project-heat-profile', {
            params: {
              projectId: selectedProject.id,
            },
          });

          console.log('API Response:', response);

          if (response.status === 200) {
            const data = response.data;
            let processedData = [];

            if (Array.isArray(data)) {
              processedData = data;
            } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
              processedData = data.data;
            } else if (data && typeof data === 'object' && Array.isArray(data.records)) {
              processedData = data.records;
            } else {
              console.warn('API returned non-array data:', data);
              processedData = [];
            }

            // Transform the data
            const transformedData = processedData.map((item) => ({
              ...item,
              projectName: item.project?.projectName || item.projectName || 'N/A',
              projectNumber: item.project?.projectId || item.projectNumber || 'N/A',
            }));

            console.log('Setting transformed project heat profiles:', transformedData);
            setProjectHeatProfiles(transformedData);
          } else {
            console.warn('API returned non-200 status:', response.status);
            setProjectHeatProfiles([]);
          }
        } catch (error) {
          console.error('Error fetching project heat profiles:', error);
          message.error('Failed to fetch project heat profiles');
          setProjectHeatProfiles([]);
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchProjectHeatProfiles();
    }
  }, [selectedProject, selectedSOW]);

  // Filter data based on search filters
  const filteredData = projectHeatProfiles.filter((item) => {
    return Object.keys(searchFilters).every((key) => {
      if (!searchFilters[key]) return true;

      const value = item[key];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchFilters[key].toLowerCase());
      }
      return false;
    });
  });

  // Handle search filter changes
  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Action handlers
  const handleView = (record) => {
    if (record.pdfReportDetails && record.pdfReportDetails.s3Url) {
      console.log('record.pdfReportDetails.s3Url:', record.pdfReportDetails.s3Url);
      window.open(record.pdfReportDetails.s3Url, '_blank');
    } else {
      message.warning('PDF report not available for this record.');
    }
  };

  const handleViewText = (record) => {
    if (record.textFileDetails && record.textFileDetails.s3Url) {
      window.open(record.textFileDetails.s3Url, '_blank');
    } else {
      message.warning('Text file not available for this record.');
    }
  };

  const handleViewImage = (record) => {
    if (record.imageFileDetails && record.imageFileDetails.s3Url) {
      window.open(record.imageFileDetails.s3Url, '_blank');
    } else {
      message.warning('Image file not available for this record.');
    }
  };

  const handleEdit = (record) => {
    console.log('Edit record:', record);
    setIsEditLoading(true);

    // Show updating message
    message.loading('Loading heat profile for editing...', 1.5).then(() => {
      if (onEdit) {
        onEdit(record);
      } else {
        message.info('Edit functionality will be implemented');
      }
      setIsEditLoading(false);
    });
  };

  const handleShare = (record) => {
    setSelectedRecord(record);
    setIsReportModalVisible(true);
  };

  const handleCopy = (record) => {
    // Copy record details to clipboard
    const recordInfo = `Heat Profile Details:
Report Number: ${record.reportNumber || 'N/A'}
Project Name: ${record.projectName || 'N/A'}
Project Number: ${record.projectNumber || 'N/A'}
Report Author: ${record.reportAuthor || 'N/A'}
Material: ${record.material || 'N/A'}
Trial Date: ${record.trailDate ? moment(record.trailDate).format('YYYY-MM-DD') : 'N/A'}
Created Date: ${record.createdAt ? moment(record.createdAt).format('YYYY-MM-DD') : 'N/A'}`;

    navigator.clipboard
      .writeText(recordInfo)
      .then(() => {
        message.success('Heat profile details copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy to clipboard');
      });
  };

  const handleSendReport = async (emailData) => {
    try {
      console.log('Sending report for record:', selectedRecord);
      const requestBody = {
        id: selectedRecord.id,
        recipientEmails: emailData.emails,
        subject: emailData.subject || `Heat Profile Report - ${selectedRecord.reportNumber}`,
        message:
          emailData.message ||
          'Please find attached the heat profile report for review and approval.',
        includeAttachments: true,
      };

      const response = await axiosConfig.post('/project-heat-profile/report', requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        message.success('Report sent successfully');
        setIsReportModalVisible(false);
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Error sending report:', error);
      message.error('Failed to send report');
    }
  };

  const showDeleteModal = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await axiosConfig.delete(`/project-heat-profile/${selectedRecord.id}`);
      message.success('Project Heat Profile deleted successfully');

      // Refresh the list by removing the deleted item from local state
      setProjectHeatProfiles((prev) => prev.filter((item) => item.id !== selectedRecord.id));
    } catch (error) {
      message.error('Failed to delete Project Heat Profile');
    }
    setIsDeleteLoading(false);
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  // Create search inputs for table header (no search icon)
  const createSearchInput = (field, placeholder) => (
    <div className="searchEquip">
      <Input
        placeholder={placeholder}
        value={searchFilters[field]}
        onChange={(e) => handleSearchChange(field, e.target.value)}
        allowClear
      />
    </div>
  );
  // Custom styles for search input to match provided design
  const style = document.createElement('style');
  style.innerHTML = `
.searchEquip .ant-input-affix-wrapper .ant-input {
    background: #0000 !important;
    border-radius: 0 !important;
}
.ant-input-affix-wrapper > .ant-input:not(textarea) {
    padding: 0 !important;
}
.ant-input:placeholder-shown {
    font-size: 14px !important;
    text-overflow: ellipsis;
}
.ant-input-affix-wrapper > .ant-input {
    border: none !important;
    font-size: inherit !important;
    outline: none !important;
}
`;
  if (typeof window !== 'undefined' && !document.getElementById('custom-search-equip-style')) {
    style.id = 'custom-search-equip-style';
    document.head.appendChild(style);
  }

  // Create date picker input for date fields
  const createDateInput = (field, placeholder) => (
    <DatePicker
      placeholder={placeholder}
      value={searchFilters[field] ? moment(searchFilters[field]) : null}
      onChange={(date) => handleSearchChange(field, date ? date.format('YYYY-MM-DD') : '')}
      style={{ width: '100%', marginBottom: 8 }}
      allowClear
    />
  );

  // Table columns with search filters
  const columns = [
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Report Number</div>
          {createSearchInput('reportNumber', 'Search')}
        </div>
      ),
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 180,
      render: (text) => (
        <Text strong style={{ color: 'white' }}>
          {text || 'N/A'}
        </Text>
      ),
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Project Name</div>
          {createSearchInput('projectName', 'Search')}
        </div>
      ),
      dataIndex: 'projectName',
      key: 'projectName',
      width: 200,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>
            Project Number
          </div>
          {createSearchInput('projectNumber', 'Search')}
        </div>
      ),
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Report Author</div>
          {createSearchInput('reportAuthor', 'Search')}
        </div>
      ),
      dataIndex: 'reportAuthor',
      key: 'reportAuthor',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Material</div>
          {createSearchInput('material', 'Search')}
        </div>
      ),
      dataIndex: 'material',
      key: 'material',
      width: 180,
      render: (text) => text || '—',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Trial Date</div>
          {createDateInput('trialDate', 'Select date')}
        </div>
      ),
      dataIndex: 'trailDate',
      key: 'trailDate',
      width: 120,
      render: (text) => {
        try {
          return text ? moment(text).format('YYYY MMM DD') : 'N/A';
        } catch {
          return 'N/A';
        }
      },
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Created Date</div>
          {createDateInput('createdDate', 'Select date')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (text) => {
        try {
          return text ? moment(text).format('YYYY MMM DD') : 'N/A';
        } catch {
          return 'N/A';
        }
      },
    },
    {
      title: <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Files</div>,
      key: 'files',
      width: 100,
      render: (_, record) => (
        <Space>
          {record.pdfReportDetails?.s3Url ? (
            <Tooltip title="View PDF">
              <FilePdfOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleView(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="PDF not available">
              <FilePdfOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
          {record.textFileDetails?.s3Url ? (
            <Tooltip title="View Document">
              <FileTextOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleViewText(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Document not available">
              <FileTextOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
          {record.imageFileDetails?.s3Url ? (
            <Tooltip title="View Images">
              <FileImageOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleViewImage(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Images not available">
              <FileImageOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Actions</div>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              loading={isEditLoading}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => showDeleteModal(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Share">
            <Button
              type="link"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => handleShare(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Copy">
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        background: '#1a1a1a',
        minHeight: '100vh',
        padding: '0px',
        color: 'white',
      }}
    >
      {/* Header */}
      <Header
        style={{
          background: '#2d2d2d',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #404040',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={
              <ArrowLeftOutlined
                style={{ color: '#ff4d4f', fontSize: '18px', marginRight: '12px' }}
              />
            }
            onClick={onBack}
            style={{
              borderColor: 'transparent',
            }}
          />

          <Title level={4} style={{ color: 'white', margin: 0 }}>
            List of Heat profile
          </Title>
        </div>

        <div className="new-calibration">
          <Button
            onClick={() => {
              console.log('Create Heat Profile button clicked in styled component');
              console.log('onCreateNew function:', onCreateNew);
              if (typeof onCreateNew === 'function') {
                onCreateNew();
              } else {
                console.error('onCreateNew is not a function:', onCreateNew);
              }
            }}
            className="ps-3 add-new-calibration"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: 400 }}
          >
            <span
              className="icon-22"
              style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}
            >
              <svg>
                <use xlinkHref="#add" />
              </svg>
            </span>
            Create Heat Profile
          </Button>
        </div>
      </Header>

      {/* Content */}

      <Content style={{ padding: '12px', background: '#1a1a1a' }}>
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={isLoadingData}
          rowKey="id"
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          style={{
            background: '#1a1a1a',
            '.ant-table': {
              background: '#1a1a1a',
            },
            '.ant-table-thead > tr > th': {
              background: '#2d2d2d',
              borderBottom: '1px solid #404040',
              color: '#ff4d4f',
            },
            '.ant-table-tbody > tr > td': {
              background: '#1a1a1a',
              borderBottom: '1px solid #404040',
              color: 'white',
            },
            '.ant-table-tbody > tr:hover > td': {
              background: '#2d2d2d',
            },
          }}
        />
      </Content>

      {/* Email Report Modal */}
      <Modal
        title="Send Heat Profile Report"
        open={isReportModalVisible}
        onCancel={() => {
          setIsReportModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={600}
      >
        <EmailReportForm
          record={selectedRecord}
          onSend={handleSendReport}
          onCancel={() => {
            setIsReportModalVisible(false);
            setSelectedRecord(null);
          }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Project Heat Profile"
        open={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={cancelDelete}
        confirmLoading={isDeleteLoading}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this Project Heat Profile?</p>
        {selectedRecord && (
          <p>
            <Text strong>Report Number:</Text>{' '}
            {typeof selectedRecord.reportNumber === 'string' ? selectedRecord.reportNumber : 'N/A'}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default ProjectHeatProfileList;
